import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { MapPin } from "@phosphor-icons/react";

// Cascading Country -> State/Province -> County/District -> City/Village picker.
// Each field is a free-text input backed by a <datalist> of existing values,
// so the user can pick from our data OR add a new value manually.
export function LocationPicker({ value = {}, onChange }) {
  const [opts, setOpts] = useState({ country: [], state: [], district: [], city: [] });
  const { country = "", state = "", district = "", city = "" } = value;

  const fetchLevel = (level, params) =>
    api.get("/public/location-options", { params: { level, ...params } })
      .then((r) => setOpts((o) => ({ ...o, [level]: r.data.options || [] })))
      .catch(() => {});

  useEffect(() => { fetchLevel("country", {}); }, []);
  useEffect(() => { if (country) fetchLevel("state", { country }); }, [country]);
  useEffect(() => { if (country && state) fetchLevel("district", { country, state }); }, [country, state]);
  useEffect(() => { if (country && state && district) fetchLevel("city", { country, state, district }); }, [country, state, district]);

  const merge = (patch) => onChange({ ...value, ...patch });

  return (
    <div className="rounded-xl border border-border/60 bg-muted/30 p-4">
      <div className="mb-2 flex items-center gap-1.5"><MapPin size={15} className="text-secondary" /><Label>Location tree</Label></div>
      <p className="mb-3 text-xs text-muted-foreground">Pick from existing entries or type a new one — this keeps our directory structured.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label className="text-xs">Country</Label>
          <Input list="loc-country" className="mt-1" value={country} placeholder="Select or type a country"
            onChange={(e) => merge({ country: e.target.value, state: "", district: "", city: "" })} data-testid="loc-country-input" />
          <datalist id="loc-country">{opts.country.map((o) => <option key={o} value={o} />)}</datalist>
        </div>
        <div>
          <Label className="text-xs">State / Province</Label>
          <Input list="loc-state" className="mt-1" value={state} disabled={!country} placeholder={country ? "Select or type" : "Choose country first"}
            onChange={(e) => merge({ state: e.target.value, district: "", city: "" })} data-testid="loc-state-input" />
          <datalist id="loc-state">{opts.state.map((o) => <option key={o} value={o} />)}</datalist>
        </div>
        <div>
          <Label className="text-xs">County / District</Label>
          <Input list="loc-district" className="mt-1" value={district} disabled={!state} placeholder={state ? "Select or type" : "Choose state first"}
            onChange={(e) => merge({ district: e.target.value, city: "" })} data-testid="loc-district-input" />
          <datalist id="loc-district">{opts.district.map((o) => <option key={o} value={o} />)}</datalist>
        </div>
        <div>
          <Label className="text-xs">City / Village</Label>
          <Input list="loc-city" className="mt-1" value={city} disabled={!district} placeholder={district ? "Select or type" : "Choose district first"}
            onChange={(e) => merge({ city: e.target.value })} data-testid="loc-city-input" />
          <datalist id="loc-city">{opts.city.map((o) => <option key={o} value={o} />)}</datalist>
        </div>
      </div>
    </div>
  );
}

export default LocationPicker;
